import type { GameState, Facing, Move, Player, PieceType } from './types'
import { isValidSquare } from './Board'

// Piece upgrade ladder (king excluded — immune to pickups)
const UPGRADE_LADDER: PieceType[] = ['pawn', 'knight', 'bishop', 'rook', 'queen']

function upgradePieceType(type: PieceType): PieceType {
  const idx = UPGRADE_LADDER.indexOf(type)
  if (idx === -1 || idx === UPGRADE_LADDER.length - 1) return type  // king or already queen
  return UPGRADE_LADDER[idx + 1]
}

// Advance delta per facing direction
const ADVANCE: Record<Facing, [number, number]> = {
  UP:    [0, -1],
  DOWN:  [0, +1],
  LEFT:  [-1, 0],
  RIGHT: [+1, 0],
}

// Perpendicular deltas for pawn captures (two diagonals relative to facing)
function pawnCaptureDirs(facing: Facing): Array<[number, number]> {
  switch (facing) {
    case 'UP':    return [[-1, -1], [+1, -1]]
    case 'DOWN':  return [[-1, +1], [+1, +1]]
    case 'LEFT':  return [[-1, -1], [-1, +1]]
    case 'RIGHT': return [[+1, -1], [+1, +1]]
  }
}

/**
 * Get all pseudo-legal destination squares for the piece at fromKey.
 * "Pseudo-legal" means we don't yet filter moves that leave the king in check.
 * Check filtering is added in M2 (rules.ts).
 *
 * Returns an array of "col,row" keys representing valid destinations.
 */
export function getLegalMoves(state: GameState, fromKey: string): string[] {
  const square = state.squares[fromKey]
  if (!square || !square.piece) return []

  const { col, row } = square
  const piece = square.piece
  const { boardSize, squares, players } = state

  function key(c: number, r: number) { return `${c},${r}` }
  function inBounds(c: number, r: number) { return isValidSquare(c, r, boardSize) }
  function isEmpty(c: number, r: number) { return inBounds(c, r) && squares[key(c, r)]?.piece === null }
  function isEnemy(c: number, r: number) {
    if (!inBounds(c, r)) return false
    const p = squares[key(c, r)]?.piece
    return p !== null && p !== undefined && p.owner !== piece.owner
  }
  function isOwn(c: number, r: number) {
    if (!inBounds(c, r)) return false
    const p = squares[key(c, r)]?.piece
    return p !== null && p !== undefined && p.owner === piece.owner
  }

  const moves: string[] = []

  function addIfFree(c: number, r: number) {
    if (isEmpty(c, r)) moves.push(key(c, r))
  }
  function addIfCapture(c: number, r: number) {
    if (isEnemy(c, r)) moves.push(key(c, r))
  }
  function addIfFreeOrCapture(c: number, r: number) {
    if (!inBounds(c, r) || isOwn(c, r)) return
    moves.push(key(c, r))
  }

  // Slide in a direction until hitting edge or a piece
  function slide(dc: number, dr: number) {
    let c = col + dc
    let r = row + dr
    while (inBounds(c, r)) {
      if (squares[key(c, r)]?.piece !== null) {
        if (isEnemy(c, r)) moves.push(key(c, r))  // can capture, then stop
        break
      }
      moves.push(key(c, r))
      c += dc
      r += dr
    }
  }

  switch (piece.type) {
    case 'pawn': {
      const [dc, dr] = ADVANCE[piece.facing]
      const fc = col + dc
      const fr = row + dr

      // Single advance
      if (isEmpty(fc, fr)) {
        addIfFree(fc, fr)
        // Double advance from starting position
        if (!piece.hasMoved) {
          const fc2 = col + dc * 2
          const fr2 = row + dr * 2
          if (isEmpty(fc2, fr2)) {
            moves.push(key(fc2, fr2))
          }
        }
      }

      // Diagonal captures
      for (const [cdc, cdr] of pawnCaptureDirs(piece.facing)) {
        const cc = col + cdc
        const cr = row + cdr
        addIfCapture(cc, cr)
        // En passant
        const player = players[piece.owner]
        if (player.enPassantTarget === key(cc, cr)) {
          moves.push(key(cc, cr))
        }
      }
      break
    }

    case 'rook':
      slide(1, 0); slide(-1, 0); slide(0, 1); slide(0, -1)
      break

    case 'bishop':
      slide(1, 1); slide(1, -1); slide(-1, 1); slide(-1, -1)
      break

    case 'queen':
      slide(1, 0); slide(-1, 0); slide(0, 1); slide(0, -1)
      slide(1, 1); slide(1, -1); slide(-1, 1); slide(-1, -1)
      break

    case 'knight': {
      const knightJumps: Array<[number, number]> = [
        [+2, +1], [+2, -1], [-2, +1], [-2, -1],
        [+1, +2], [+1, -2], [-1, +2], [-1, -2],
      ]
      for (const [dc, dr] of knightJumps) {
        addIfFreeOrCapture(col + dc, row + dr)
      }
      break
    }

    case 'king': {
      const kingDirs: Array<[number, number]> = [
        [0, 1], [0, -1], [1, 0], [-1, 0],
        [1, 1], [1, -1], [-1, 1], [-1, -1],
      ]
      for (const [dc, dr] of kingDirs) {
        addIfFreeOrCapture(col + dc, row + dr)
      }
      break
    }
  }

  return moves
}

/**
 * Apply a move to a game state and return the new state.
 * This is a pure function — returns a new state without mutating the input.
 * Does not validate legality (call getLegalMoves first), does not detect check.
 */
export function applyMoveToState(state: GameState, move: Move): GameState {
  const { from, to, player, promotion } = move
  const fromSquare = state.squares[from]
  const toSquare = state.squares[to]

  if (!fromSquare?.piece) {
    throw new Error(`No piece at ${from}`)
  }

  const piece = fromSquare.piece
  const newSquares = { ...state.squares }

  // Track captured pieces
  const newCapturedBy = { ...state.capturedBy }

  // Regular capture: check if destination square has an enemy piece
  const capturedPiece = toSquare?.piece
  if (capturedPiece && capturedPiece.owner !== player) {
    if (!newCapturedBy[player]) {
      newCapturedBy[player] = []
    }
    newCapturedBy[player] = [...newCapturedBy[player], capturedPiece.type]
  }

  // Clear source square
  newSquares[from] = { ...fromSquare, piece: null }

  // Determine moved piece (apply promotion if applicable)
  let movedPiece = { ...piece, hasMoved: true }
  if (promotion && piece.type === 'pawn') {
    movedPiece = { ...movedPiece, type: promotion }
  }

  // Place piece at destination (capturing whatever was there)
  newSquares[to] = { ...toSquare, piece: movedPiece }

  // Handle en passant capture
  const newPlayers = state.players.map(p => ({ ...p }))
  let enPassantCaptureKey: string | null = null

  if (piece.type === 'pawn') {
    const [dc, dr] = ADVANCE[piece.facing]
    const [fromCol, fromRow] = from.split(',').map(Number)
    const [toCol, toRow] = to.split(',').map(Number)

    // Check if this was an en passant capture
    const currentPlayer = state.players[player]
    if (currentPlayer.enPassantTarget === to) {
      // Remove the captured pawn (it's one step behind the en passant target)
      // The captured pawn is at the target square minus the advance direction
      const capturedCol = toCol - dc
      const capturedRow = toRow - dr
      enPassantCaptureKey = `${capturedCol},${capturedRow}`
      if (newSquares[enPassantCaptureKey]?.piece) {
        // Track the en passant captured pawn
        if (!newCapturedBy[player]) {
          newCapturedBy[player] = []
        }
        newCapturedBy[player] = [...newCapturedBy[player], 'pawn']
        newSquares[enPassantCaptureKey] = { ...newSquares[enPassantCaptureKey], piece: null }
      }
    }

    // Set en passant target for double advance
    const deltaRow = Math.abs(toRow - fromRow)
    const deltaCol = Math.abs(toCol - fromCol)
    const isDoubleAdvance = (deltaRow === 2 || deltaCol === 2)

    // Clear en passant for all players, then set for relevant opponents
    for (const p of newPlayers) {
      p.enPassantTarget = null
    }

    if (isDoubleAdvance) {
      // The en passant target is the square the pawn passed through
      const epCol = fromCol + dc
      const epRow = fromRow + dr
      const epKey = `${epCol},${epRow}`
      // Set enPassantTarget on all opposing players (they can capture it)
      for (const p of newPlayers) {
        if (p.index !== player) {
          p.enPassantTarget = epKey
        }
      }
    }
  } else {
    // Clear en passant targets when non-pawn moves
    for (const p of newPlayers) {
      p.enPassantTarget = null
    }
  }

  // Handle castling: when king moves exactly 2 squares, move the accompanying rook
  if (piece.type === 'king') {
    const [fromColNum, fromRowNum] = from.split(',').map(Number)
    const [toColNum, toRowNum] = to.split(',').map(Number)
    const colDelta = toColNum - fromColNum
    const rowDelta = toRowNum - fromRowNum

    const moveCastlingRook = (dir: number, horizontal: boolean) => {
      let c = horizontal ? fromColNum + dir : fromColNum
      let r = horizontal ? fromRowNum : fromRowNum + dir
      const step = () => { if (horizontal) c += dir; else r += dir }
      while (c >= 0 && c < state.boardSize && r >= 0 && r < state.boardSize) {
        const rKey = `${c},${r}`
        const rPiece = state.squares[rKey]?.piece  // scan original state
        if (rPiece?.type === 'rook' && rPiece.owner === player) {
          const rookDest = horizontal
            ? `${toColNum - dir},${fromRowNum}`
            : `${fromColNum},${toRowNum - dir}`
          newSquares[rKey] = { ...newSquares[rKey], piece: null }
          newSquares[rookDest] = { ...newSquares[rookDest], piece: { ...rPiece, hasMoved: true } }
          return
        }
        if (rPiece) return  // blocked by non-rook piece
        step()
      }
    }

    if (Math.abs(colDelta) === 2 && rowDelta === 0) {
      moveCastlingRook(colDelta > 0 ? 1 : -1, true)
    } else if (colDelta === 0 && Math.abs(rowDelta) === 2) {
      moveCastlingRook(rowDelta > 0 ? 1 : -1, false)
    }
  }

  // Update castling rights if king or rook moved
  if (piece.type === 'king') {
    newPlayers[player].castlingRights = { kingSide: false, queenSide: false }
  } else if (piece.type === 'rook') {
    // Determine if kingSide or queenSide rook by position
    // (simplified: if col is high it's kingSide, low is queenSide for UP/DOWN facing;
    //  for LEFT/RIGHT facing, row-based)
    const [fromCol, fromRow] = from.split(',').map(Number)
    const playerFacing = state.players[player].facing
    if (playerFacing === 'UP' || playerFacing === 'DOWN') {
      if (fromCol >= 4) newPlayers[player].castlingRights = { ...newPlayers[player].castlingRights, kingSide: false }
      else newPlayers[player].castlingRights = { ...newPlayers[player].castlingRights, queenSide: false }
    } else {
      if (fromRow >= 4) newPlayers[player].castlingRights = { ...newPlayers[player].castlingRights, kingSide: false }
      else newPlayers[player].castlingRights = { ...newPlayers[player].castlingRights, queenSide: false }
    }
  }

  // Eliminate players whose king was captured
  const playersWithKings = new Set<number>()
  for (const sq of Object.values(newSquares)) {
    if (sq.piece?.type === 'king') playersWithKings.add(sq.piece.owner)
  }
  for (const p of newPlayers) {
    if (p.alive && !playersWithKings.has(p.index)) {
      p.alive = false
    }
  }

  // Advance turn to next alive player
  const nextActivePlayer = getNextActivePlayer(newPlayers, player)

  // Increment turnNumber when the active player wraps back to a lower index
  // (i.e. all alive players have completed one round)
  const nextTurnNumber = nextActivePlayer <= player
    ? state.turnNumber + 1
    : state.turnNumber

  // Consume pickup on destination square — always removed, but king cannot benefit from it
  let newPickups = { ...state.pickups }
  const destinationPiece = newSquares[to].piece
  if (destinationPiece && newPickups[to]) {
    const pickup = newPickups[to]
    delete newPickups[to]

    if (destinationPiece.type !== 'king') {
      let newType: PieceType = destinationPiece.type
      if (pickup.kind === 'upgrade') {
        newType = upgradePieceType(destinationPiece.type)
      } else {
        newType = pickup.to
      }

      if (newType !== destinationPiece.type) {
        newSquares[to] = { ...newSquares[to], piece: { ...destinationPiece, type: newType } }
      }
    }
  }

  return {
    ...state,
    squares: newSquares,
    players: newPlayers,
    activePlayer: nextActivePlayer,
    turnNumber: nextTurnNumber,
    moveHistory: [...state.moveHistory, { ...move, pieceType: piece.type }],
    capturedBy: newCapturedBy,
    pickups: newPickups,
  }
}

/**
 * Get the next alive player index after the current one.
 */
export function getNextActivePlayer(players: Player[], currentPlayer: number): number {
  let next = (currentPlayer + 1) % players.length
  let iterations = 0
  while (!players[next].alive && iterations < players.length) {
    next = (next + 1) % players.length
    iterations++
  }
  return next
}
